import React, { useEffect, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { StarterThumb, TEMPLATE_FONTS_HREF, thumbPalette } from './StarterThumb';

/**
 * Signup's "Pick a starting point" (UX review v8, Signup Journey): a list of
 * starters in two shelves — sites that explain, sites that publish — beside
 * one big preview with a tab per page the starter makes, so "what you get" is
 * something to click through. On a phone the list and preview become a swipe
 * carousel, one card per starter. Choosing one applies it — pages, forms,
 * header and footer, home page — as working copies, so the first preview is a
 * real site. The shape (brand site, blog, both) is implied by the starter.
 */

export const TINYCMS_STARTERS = gql`
  query TinycmsStarters {
    tinycmsStarters
  }
`;

export const APPLY_TINYCMS_STARTER = gql`
  mutation ApplyTinycmsStarter($accountId: ID!, $starterId: String!, $tagline: String, $only: String) {
    applyTinycmsStarter(accountId: $accountId, starterId: $starterId, tagline: $tagline, only: $only)
  }
`;

export interface Starter {
  id: string;
  name: string;
  blurb: string;
  shape: 'brand' | 'blog' | 'both';
  /** Which thumbnail drawing the home page gets (StarterThumb). */
  kind: string;
  /** One line on the template's personality (UX review v10). */
  mood?: string;
  tagline: string;
  look: {
    preset: string;
    colors: { background: string; text: string; accent: string; muted: string; accent2?: string | null; surface?: string | null };
    fonts: { heading: string; body: string };
    corners: 'round' | 'soft' | 'square';
    imageShape?: string | null;
    photos?: 'colour' | 'grey' | 'duotone';
  };
  pages: { title: string; slug: string; home: boolean }[];
  collections?: { slug: string; name: string }[];
  hasArticles: boolean;
}

/** Loads the templates' Google fonts into the app once, so thumbnails draw in the real faces. */
export function useTemplateFonts() {
  useEffect(() => {
    if (document.querySelector('link[data-template-fonts]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = TEMPLATE_FONTS_HREF;
    link.setAttribute('data-template-fonts', '');
    document.head.appendChild(link);
  }, []);
}

const GROUPS: { title: string; shapes: Starter['shape'][] }[] = [
  { title: 'Tell people what you do', shapes: ['brand'] },
  { title: 'Publish regularly', shapes: ['blog', 'both'] },
];

const MAKES_TAIL: Record<Starter['shape'], string> = {
  brand: 'No posts. Add a blog any time.',
  blog: 'Posts, newest first, on the home page',
  both: 'Plus a front page of stories',
};

/** The public site's font presets, for the "Aa" swatch. */
const SWATCH_FONTS: Record<string, string> = {
  serif: 'Georgia, "Times New Roman", serif',
  display: 'Georgia, "Iowan Old Style", serif',
  mono: 'ui-monospace, Menlo, monospace',
};

/** The thumbnails are drawn at this width; the preview scales them to fit. */
const THUMB_WIDTH = 300;

const pageSlug = (title: string) => title.toLowerCase().replace(/ /g, '-');

export function StarterStep({
  stepOf,
  accountId,
  siteName,
  siteAddr,
  onDone,
}: {
  stepOf: string;
  accountId?: string;
  siteName: string;
  siteAddr: string;
  onDone: () => void | Promise<void>;
}) {
  const { data, loading } = useQuery<{ tinycmsStarters: Starter[] }>(TINYCMS_STARTERS);
  const starters = data?.tinycmsStarters || [];
  useTemplateFonts();
  // Shelves in order, so the phone carousel and its dots follow the list.
  const groups = GROUPS.map((g) => ({ ...g, items: starters.filter((s) => g.shapes.includes(s.shape)) }));
  const ordered = groups.flatMap((g) => g.items);

  const [chosen, setChosen] = useState<string>('');
  const [tabs, setTabs] = useState<Record<string, string>>({});
  const [tagline, setTagline] = useState('');
  const [error, setError] = useState('');
  const [apply, { loading: applying }] = useMutation(APPLY_TINYCMS_STARTER);
  const starter = ordered.find((s) => s.id === chosen) || ordered[0];
  const pageOf = (s: Starter) => tabs[s.id] || 'Home';
  const urlOf = (s: Starter) => {
    const page = pageOf(s);
    return `${siteAddr || 'yourname'}.tinycms.ai${page === 'Home' ? '' : `/${pageSlug(page)}`}`;
  };

  // The preview scales its thumbnail to its own width (CSS zoom).
  const prevRef = useRef<HTMLDivElement | null>(null);
  const [prevWidth, setPrevWidth] = useState(0);
  useEffect(() => {
    const el = prevRef.current;
    if (!el) return;
    const measure = () => setPrevWidth(el.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [starter?.id]);
  const prevZoom = prevWidth ? Math.max(0.9, prevWidth / THUMB_WIDTH) : 1.6;

  // Phone: whichever card is centred is the pick.
  const carRef = useRef<HTMLDivElement | null>(null);
  const onCarScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!ordered.length) return;
    const i = Math.min(ordered.length - 1, Math.round(el.scrollLeft / (el.scrollWidth / ordered.length)));
    if (ordered[i] && ordered[i].id !== chosen) setChosen(ordered[i].id);
  };
  const goCard = (i: number) => {
    const el = carRef.current;
    if (el) el.scrollTo({ left: i * (el.scrollWidth / ordered.length), behavior: 'smooth' });
  };
  const chosenIndex = Math.max(0, ordered.findIndex((s) => s.id === starter?.id));
  // Once the starters arrive, the carousel starts on the chosen card.
  useEffect(() => {
    const el = carRef.current;
    if (el && ordered.length) el.scrollTo({ left: chosenIndex * (el.scrollWidth / ordered.length) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordered.length]);
  const groupOfChosen = groups.find((g) => g.items.some((s) => s.id === starter?.id)) || groups[0];
  const indexInGroup = groupOfChosen ? groupOfChosen.items.findIndex((s) => s.id === starter?.id) + 1 : 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountId || !starter) return;
    setError('');
    try {
      await apply({ variables: { accountId, starterId: starter.id, tagline: tagline.trim() || null } });
      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your starting point couldn’t be set up.');
    }
  };

  const tabRow = (s: Starter, mobile = false) =>
    s.pages.map((p) => {
      const on = p.title === pageOf(s);
      return (
        <button
          key={p.slug}
          type="button"
          className={mobile ? 'flow-st-mtab' : 'flow-st-tab'}
          data-active={on ? 'true' : undefined}
          onClick={() => setTabs((t) => ({ ...t, [s.id]: p.title }))}
        >
          {p.title}
        </button>
      );
    });

  return (
    <form onSubmit={submit}>
      <div className="flow-step-of">{stepOf}</div>
      <h1 className="flow-h1">Pick a starting point</h1>
      <p className="flow-lede" style={{ maxWidth: 620 }}>
        Each one is a real site: pages, menus and a look. Nothing goes live until you approve it,
        and your agent rewrites every word in yours.
      </p>

      {loading ? <p className="flow-hint">Loading…</p> : null}

      {starter ? (
        <>
          {/* Desktop and tablet: shelves beside one big preview. */}
          <div className="flow-st-grid">
            <div className="flow-st-list" role="radiogroup" aria-label="Starting point">
              {groups.map((g) => (
                <div key={g.title} className="flow-st-group">
                  <div className="flow-st-group-title">{g.title}</div>
                  <div className="flow-st-items">
                    {g.items.map((s) => {
                      const c = thumbPalette(s);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          role="radio"
                          aria-checked={s.id === starter.id}
                          className="flow-st-row"
                          data-active={s.id === starter.id ? 'true' : undefined}
                          onClick={() => setChosen(s.id)}
                        >
                          <span
                            className="flow-st-swatch"
                            style={{
                              background: c.bg,
                              color: c.text,
                              fontFamily: SWATCH_FONTS[s.look.fonts.heading] || undefined,
                            }}
                          >
                            Aa
                            <i style={{ background: c.accent }} />
                          </span>
                          <span className="flow-st-row-text">
                            <span className="flow-st-row-name">{s.name}</span>
                            <span className="flow-st-row-blurb">{s.blurb}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flow-st-prev">
              <div className="flow-st-frame">
                <div className="flow-st-bar">
                  <div className="flow-st-tabs">
                    <span className="flow-st-count">{starter.pages.length} pages</span>
                    {tabRow(starter)}
                  </div>
                  <span className="flow-st-url">{urlOf(starter)}</span>
                </div>
                <div ref={prevRef} className="flow-st-canvas">
                  <div style={{ zoom: prevZoom, height: 205 }}>
                    <StarterThumb
                      starter={starter}
                      page={pageOf(starter)}
                      siteName={siteName}
                      siteAddr={siteAddr}
                      onPage={(p) => setTabs((t) => ({ ...t, [starter.id]: p }))}
                    />
                  </div>
                </div>
              </div>
              <div className="flow-st-about">
                <div className="flow-st-name">{starter.name}</div>
                <div className="flow-st-blurb">{starter.mood || starter.blurb}</div>
                <div className="flow-st-makes">
                  Makes {starter.pages.length} pages · {MAKES_TAIL[starter.shape]}
                </div>
              </div>
            </div>
          </div>

          {/* Phone: swipe to choose; whichever card is centred is the pick. */}
          <div className="flow-st-mob">
            <div className="flow-st-mhead">
              <span>{groupOfChosen?.title}</span>
              <span>
                {indexInGroup} of {groupOfChosen?.items.length}
              </span>
            </div>
            <div ref={carRef} className="flow-st-car" onScroll={onCarScroll}>
              {ordered.map((s) => (
                <div key={s.id} className="flow-st-card">
                  <div className="flow-st-card-thumb">
                    <div style={{ zoom: 1.1, height: 180 }}>
                      <StarterThumb
                        starter={s}
                        page={pageOf(s)}
                        siteName={siteName}
                        siteAddr={siteAddr}
                        onPage={(p) => setTabs((t) => ({ ...t, [s.id]: p }))}
                      />
                    </div>
                  </div>
                  <div className="flow-st-mtabs">{tabRow(s, true)}</div>
                  <div className="flow-st-card-text">
                    <span className="flow-st-row-name" style={{ fontSize: 17 }}>
                      {s.name}
                    </span>
                    <span className="flow-st-row-blurb" style={{ fontSize: 14 }}>
                      {s.blurb}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flow-st-dots">
              {groups.map((g) => (
                <span key={g.title}>
                  {g.items.map((s) => {
                    const i = ordered.findIndex((o) => o.id === s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        aria-label={s.name}
                        data-active={i === chosenIndex ? 'true' : undefined}
                        onClick={() => goCard(i)}
                      />
                    );
                  })}
                </span>
              ))}
            </div>
          </div>

          <div className="flow-field flow-st-tagline">
            <label className="flow-label" htmlFor="starter-tagline">
              One line about {siteName || 'your site'} <span className="flow-optional">(optional)</span>
            </label>
            <input
              id="starter-tagline"
              className="flow-input"
              value={tagline}
              maxLength={160}
              placeholder={starter.tagline}
              onChange={(e) => setTagline(e.target.value)}
            />
            <span className="flow-hint">Goes on your home page and under your site name. Easy to change later.</span>
          </div>
        </>
      ) : null}

      {error ? <p className="flow-error flow-submit-error">{error}</p> : null}

      <button type="submit" className="flow-btn" disabled={!starter || applying || !accountId}>
        {applying ? 'Setting up your pages…' : starter ? `Start with ${starter.name}` : 'Continue'}
      </button>
      {starter ? (
        <p className="flow-fine flow-st-fine">
          Makes {starter.pages.length} pages: {starter.pages.map((p) => p.title).join(', ')}. All drafts, none live.
        </p>
      ) : null}
    </form>
  );
}
